/**
 * OfflineSyncIndicator — small pill near the top of the review surface
 * that surfaces the offline-queue state. Three visual states:
 *   - Offline + N pending → amber, with a manual Sync button (disabled)
 *   - Online + N pending → blue, with a tap-to-sync button
 *   - Online + 0 pending → muted "All synced" (auto-hides after 4s if
 *       the user just synced; rendered as a quiet confirmation)
 *   - 'syncing' → spinner pill regardless of count
 */
import { CloudOff, RefreshCw, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const TONE = {
  offline: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-600 dark:text-amber-400',
    Icon: CloudOff,
  },
  pending: {
    bg: 'bg-[#002FA7]/10',
    border: 'border-[#002FA7]/30',
    text: 'text-[#002FA7] dark:text-blue-300',
    Icon: RefreshCw,
  },
  synced: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    Icon: Check,
  },
  error: {
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    text: 'text-rose-600 dark:text-rose-400',
    Icon: AlertCircle,
  },
  syncing: {
    bg: 'bg-[#002FA7]/10',
    border: 'border-[#002FA7]/30',
    text: 'text-[#002FA7] dark:text-blue-300',
    Icon: Loader2,
  },
};

const formatRelative = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleDateString();
  } catch { return null; }
};

const OfflineSyncIndicator = ({
  isOnline,
  pendingCount,
  lastSyncAt,
  syncStatus,
  onSync,
}) => {
  // Decide which tone to show.
  let toneKey;
  let label;
  if (syncStatus === 'syncing') {
    toneKey = 'syncing';
    label = 'Syncing reviews…';
  } else if (!isOnline) {
    toneKey = 'offline';
    label = pendingCount > 0
      ? `Offline · ${pendingCount} pending`
      : 'Offline · reviews will sync later';
  } else if (pendingCount > 0) {
    toneKey = 'pending';
    label = `${pendingCount} review${pendingCount === 1 ? '' : 's'} ready to sync`;
  } else if (syncStatus === 'error') {
    toneKey = 'error';
    label = 'Sync failed · we\u2019ll retry';
  } else {
    toneKey = 'synced';
    const rel = formatRelative(lastSyncAt);
    label = rel ? `All synced · ${rel}` : 'All reviews synced';
  }

  const tone = TONE[toneKey];
  const Icon = tone.Icon;

  return (
    <div
      data-testid="offline-sync-indicator"
      data-sync-state={toneKey}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${tone.bg} ${tone.border} ${tone.text} text-xs sm:text-sm font-medium`}
      role="status"
      aria-live="polite"
    >
      <Icon
        className={`w-3.5 h-3.5 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      <span data-testid="offline-sync-label">{label}</span>
      {isOnline && pendingCount > 0 && syncStatus !== 'syncing' && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onSync}
          data-testid="offline-sync-now"
          aria-label="Sync pending reviews now"
          className="h-6 px-2 text-xs hover:bg-white/40 dark:hover:bg-white/10"
        >
          Sync
        </Button>
      )}
    </div>
  );
};

export default OfflineSyncIndicator;
